import { normalizeApplicationDates, toDateInput } from '@shared/consumerPlan';
import { WaterlilyClient, createQueryKeys } from '@client/api/waterlily';

export const consumerPlanKeys = createQueryKeys('consumerPlan');
export const consumerQuoteKeys = createQueryKeys('consumerQuotes');

export interface PolicyReviewCheckoutRequest {
  reviewType: ConsumerPolicyReviewType;
  documents: ConsumerPolicyReviewDocument[];
  responseDeadline: string | null;
  successPath: string;
  cancelPath: string;
}

export interface PremiumRangeRequest {
  paymentSchedule: ConsumerPaymentSchedule;
  minPremium: number;
  maxPremium: number;
  email: string;
}

export const consumerApi = {
  getPlan: async (clientId: string): Promise<ConsumerPlanResponse> => {
    const response = await WaterlilyClient.get<ConsumerPlanResponse>(`/client/${clientId}/consumer-plan`);
    const { application, partner } = response.state;
    return {
      ...response,
      state: {
        ...response.state,
        application: application && normalizeApplicationDates(application),
        partner: partner && { ...partner, birthDate: toDateInput(partner.birthDate) },
      },
    };
  },

  savePlan: (clientId: string, state: ConsumerPlanState) =>
    WaterlilyClient.put<ConsumerPlanState>(`/client/${clientId}/consumer-plan`, { state }),

  getQuotes: (clientId: string) =>
    WaterlilyClient.get<ConsumerQuotesResponse>(`/client/${clientId}/consumer-quotes`),

  submitApplication: (
    clientId: string,
    body: { coverage: ConsumerApplicationCoverage; answers: ConsumerApplicationAnswers; consentIds: string[] },
  ) =>
    WaterlilyClient.post<ConsumerApplicationSubmission>(
      `/client/${clientId}/consumer-application`,
      body,
    ),

  getPolicyReviewUploadUrl: (clientId: string, body: { fileName: string; contentType: string }) =>
    WaterlilyClient.post<ConsumerPolicyReviewDocumentUpload>(
      `/client/${clientId}/consumer-policy-review-document-url`,
      body,
    ),

  startPolicyReviewCheckout: (clientId: string, body: PolicyReviewCheckoutRequest) =>
    WaterlilyClient.post<ConsumerPolicyReviewCheckout>(
      `/client/${clientId}/consumer-policy-review`,
      body,
    ),

  requestPremiumRange: (clientId: string, body: PremiumRangeRequest) =>
    WaterlilyClient.post<{ requested: true }>(`/client/${clientId}/consumer-quote-request`, body),

  saveCustomInferences: (clientId: string, clientCustomInferences: PutClientCustomInferencesBodyProps['clientCustomInferences']) =>
    WaterlilyClient.put<unknown>(`/client/${clientId}/client-custom-inferences`, { clientId, clientCustomInferences }),

  getPolicyReviewDocumentDownloadUrl: (reviewId: string, documentIndex: number) =>
    WaterlilyClient.post<{ url: string; fileName: string }>('/consumer-policy-review-document-download-url', {
      reviewId,
      documentIndex,
    }),

  resetFlowForWizard: (clientId: string) =>
    WaterlilyClient.delete<{ clientId: string }>(`/client/${clientId}/consumer-flow`),
};
